// src/hooks/PracticeTimerProvider.jsx
/**
 * Global Practice Timer Context (V4)
 *
 * TWO-LAYER ARCHITECTURE (fixes refresh bug + anti-cheat):
 *   1. localStorage (cache)  → instant hydration on mount, no flash of 10:00
 *   2. Supabase (truth)      → cross-device sync + anti-cheat authority
 *
 * Anti-cheat rule: on every page load we take MAX(local, remote) for
 * seconds_used. So the user can't clear localStorage to reset, and can't
 * refresh mid-session to skip the timer — whichever store has the higher
 * "used" count wins.
 *
 * ✅ Single timer instance for the whole app
 * ✅ Wall-clock based (requestAnimationFrame, no drift)
 * ✅ Persists across level nav, videos, AND refreshes
 * ✅ Auto-resumes on page load if user was mid-session
 * ✅ localStorage written every tick → refresh-proof
 * ✅ Flushes to Supabase on beforeunload/pagehide/visibilitychange
 * ✅ Fixes login race: startTimer waits for hydration
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { supabase } from "../supabaseClient";
import { useAuth } from "../auth/AuthProvider";
import { usePremiumStatus } from "./usePremiumStatus";
import { getSmartMessage } from "../utils/panMessageGenerator";

// ── constants ──────────────────────────────────────────────
const DAILY_LIMIT_SECONDS = 600; // 10 minutes
const SYNC_INTERVAL_MS = 10_000; // sync to Supabase every 10 s

// localStorage key — scoped per user+date
const LS_KEY_PREFIX = "pwp_timer_v4_";

// ── helpers ────────────────────────────────────────────────
const getToday = () => new Date().toISOString().split("T")[0];

function lsKeyFor(userId, date) {
  return `${LS_KEY_PREFIX}${userId || "anon"}_${date}`;
}

function readLocalCache(userId, date) {
  if (typeof window === "undefined" || !userId) return null;
  try {
    const raw = window.localStorage.getItem(lsKeyFor(userId, date));
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || typeof data !== "object") return null;
    const secondsUsed = Number(data.seconds_used) || 0;
    return {
      seconds_used: Math.max(0, Math.min(DAILY_LIMIT_SECONDS, secondsUsed)),
      last_update: Number(data.last_update) || 0,
    };
  } catch {
    return null;
  }
}

function writeLocalCache(userId, date, secondsUsed) {
  if (typeof window === "undefined" || !userId) return;
  try {
    window.localStorage.setItem(
      lsKeyFor(userId, date),
      JSON.stringify({
        seconds_used: Math.max(0, Math.min(DAILY_LIMIT_SECONDS, secondsUsed)),
        last_update: Date.now(),
      })
    );
  } catch {}
}

function cleanupOldLocalKeys(userId) {
  if (typeof window === "undefined" || !userId) return;
  try {
    const today = getToday();
    const prefix = `${LS_KEY_PREFIX}${userId}_`;
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        const date = key.substring(prefix.length);
        if (date !== today) {
          window.localStorage.removeItem(key);
        }
      }
    }
  } catch {}
}

// ── context ────────────────────────────────────────────────
const PracticeTimerContext = createContext(null);

export function usePracticeTimer() {
  return useContext(PracticeTimerContext);
}

// ── provider ───────────────────────────────────────────────
export function PracticeTimerProvider({ children }) {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  // --- state ---
  const [isActive, setIsActive] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(DAILY_LIMIT_SECONDS);
  const [totalSecondsUsed, setTotalSecondsUsed] = useState(0);

  // ✅ Premium state now sourced from canonical usePremiumStatus hook.
  //    Same Supabase `subscriptions` lookup as before, just centralised so
  //    other components can read it directly without going through the timer.
  const { isPremium, premiumLoaded } = usePremiumStatus();

  const [hydrated, setHydrated] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [timeExpired, setTimeExpired] = useState(false);

  // --- refs (for sync handlers fired outside React) ---
  const sessionDbId = useRef(null);
  const tickRaf = useRef(null);
  const lastTickTs = useRef(null);
  const syncIntervalRef = useRef(null);
  const warningsSent = useRef({
    twoMinutes: false,
    oneMinute: false,
    thirtySeconds: false,
    expired: false,
  });
  const fractionalAccum = useRef(0);
  const timerDateRef = useRef(null);

  // Mirrors for non-React access (beforeunload etc.)
  const totalSecondsUsedRef = useRef(0);
  const remainingSecondsRef = useRef(DAILY_LIMIT_SECONDS);
  const userIdRef = useRef(null);
  const isPremiumRef = useRef(false);
  const isActiveRef = useRef(false);

  useEffect(() => { totalSecondsUsedRef.current = totalSecondsUsed; }, [totalSecondsUsed]);
  useEffect(() => { remainingSecondsRef.current = remainingSeconds; }, [remainingSeconds]);
  useEffect(() => { userIdRef.current = userId; }, [userId]);
  useEffect(() => { isPremiumRef.current = isPremium; }, [isPremium]);
  useEffect(() => { isActiveRef.current = isActive; }, [isActive]);

  // Callbacks (set by LevelTemplate)
  const onTimeWarningRef = useRef(null);
  const onTimeExpiredRef = useRef(null);
  const onPanMessageRef = useRef(null);

  // ── 1. Premium check is now handled by usePremiumStatus() above ─────
  //     (pulls from Supabase `subscriptions` table, cross-device by user_id)

  // ── 2. HYDRATE on mount/login: localStorage + Supabase (anti-cheat MAX) ──
  useEffect(() => {
    // Reset hydration flag when userId changes
    setHydrated(false);

    if (!premiumLoaded) return;

    // No user → default state, mark hydrated
    if (!userId) {
      setRemainingSeconds(DAILY_LIMIT_SECONDS);
      setTotalSecondsUsed(0);
      setTimeExpired(false);
      setIsActive(false);
      sessionDbId.current = null;
      warningsSent.current = {
        twoMinutes: false,
        oneMinute: false,
        thirtySeconds: false,
        expired: false,
      };
      setHydrated(true);
      return;
    }

    // Premium → unlimited, no timer
    if (isPremium) {
      setRemainingSeconds(Infinity);
      setTotalSecondsUsed(0);
      setTimeExpired(false);
      setHydrated(true);
      return;
    }

    let alive = true;
    const today = getToday();

    // Clean up yesterday's entries
    cleanupOldLocalKeys(userId);

    // ─── STEP 1: LOCAL CACHE (instant, synchronous) ───
    const localData = readLocalCache(userId, today);
    const localUsed = localData?.seconds_used ?? 0;

    if (localUsed > 0) {
      const localRemaining = Math.max(0, DAILY_LIMIT_SECONDS - localUsed);
      setRemainingSeconds(localRemaining);
      setTotalSecondsUsed(localUsed);
      setTimeExpired(localRemaining <= 0);
      warningsSent.current = {
        twoMinutes: localRemaining <= 120,
        oneMinute: localRemaining <= 60,
        thirtySeconds: localRemaining <= 30,
        expired: localRemaining <= 0,
      };
      timerDateRef.current = today;

      // Auto-resume countdown if there's time left
      if (localRemaining > 0) {
        setIsActive(true);
        console.log(`⏱️ Timer resumed from cache: ${localRemaining}s remaining`);
      }
    }

    // ─── STEP 2: SUPABASE (authoritative, async anti-cheat check) ───
    async function verifyWithSupabase() {
      try {
        const { data, error } = await supabase
          .from("daily_practice_sessions")
          .select("*")
          .eq("user_id", userId)
          .eq("date", today)
          .maybeSingle();

        if (!alive) return;

        if (error) {
          console.warn("Timer hydrate: Supabase error", error);
          setHydrated(true);
          return;
        }

        const remoteUsed = data?.seconds_used ?? 0;
        // ✅ ANTI-CHEAT: take MAX of local and remote
        const authoritativeUsed = Math.max(localUsed, remoteUsed);
        const authoritativeRemaining = Math.max(0, DAILY_LIMIT_SECONDS - authoritativeUsed);

        if (data) {
          sessionDbId.current = data.id;
        }

        // If Supabase has stricter (higher used) value, update UI
        if (authoritativeUsed > localUsed) {
          console.log(`🛡️ Supabase stricter: ${authoritativeUsed}s used (local had ${localUsed}s)`);
          setRemainingSeconds(authoritativeRemaining);
          setTotalSecondsUsed(authoritativeUsed);
          setTimeExpired(authoritativeRemaining <= 0);
          warningsSent.current = {
            twoMinutes: authoritativeRemaining <= 120,
            oneMinute: authoritativeRemaining <= 60,
            thirtySeconds: authoritativeRemaining <= 30,
            expired: authoritativeRemaining <= 0,
          };

          // Update local cache to match
          writeLocalCache(userId, today, authoritativeUsed);

          // Resume or stop based on remaining time
          if (authoritativeRemaining > 0 && authoritativeUsed > 0) {
            setIsActive(true);
          } else if (authoritativeRemaining <= 0) {
            setIsActive(false);
          }
        }

        timerDateRef.current = today;
        setHydrated(true);
      } catch (err) {
        console.error("Timer hydrate failed:", err);
        if (alive) setHydrated(true);
      }
    }

    verifyWithSupabase();

    return () => { alive = false; };
  }, [premiumLoaded, userId, isPremium]);

  // ── 3. Get or create Supabase session ──────────────────
  const getOrCreateSession = useCallback(async () => {
    if (!userId || isPremium) return null;
    const today = getToday();

    try {
      const { data: existing, error: fetchError } = await supabase
        .from("daily_practice_sessions")
        .select("*")
        .eq("user_id", userId)
        .eq("date", today)
        .maybeSingle();

      if (!fetchError && existing) {
        sessionDbId.current = existing.id;
        return existing;
      }

      // Create new — use local cache if any exists
      const localData = readLocalCache(userId, today);
      const startingUsed = localData?.seconds_used ?? 0;
      const startingRemaining = Math.max(0, DAILY_LIMIT_SECONDS - startingUsed);

      const { data: newSession, error: createError } = await supabase
        .from("daily_practice_sessions")
        .insert({
          user_id: userId,
          date: today,
          seconds_remaining: startingRemaining,
          seconds_used: startingUsed,
          is_active: false,
        })
        .select()
        .single();

      if (createError) {
        console.error("Timer: error creating session", createError);
        return null;
      }

      sessionDbId.current = newSession.id;
      return newSession;
    } catch (err) {
      console.error("Timer: getOrCreateSession error", err);
      return null;
    }
  }, [userId, isPremium]);

  // ── 4. Sync to Supabase (uses refs so handlers outside React can call it) ──
  const syncToSupabase = useCallback(async () => {
    const uid = userIdRef.current;
    if (!uid || isPremiumRef.current) return;

    if (!sessionDbId.current) {
      await getOrCreateSession();
      if (!sessionDbId.current) return;
    }

    const used = totalSecondsUsedRef.current;
    const remaining = Math.max(0, remainingSecondsRef.current);

    try {
      const { error } = await supabase
        .from("daily_practice_sessions")
        .update({
          seconds_used: used,
          seconds_remaining: remaining,
          last_activity_time: new Date().toISOString(),
          is_active: isActiveRef.current,
        })
        .eq("id", sessionDbId.current);

      if (!error) setLastSyncTime(Date.now());
    } catch (err) {
      console.error("Timer sync error:", err);
    }
  }, [getOrCreateSession]);

  // ── 5. Start timer ──────────────────────────────────────
  const startTimer = useCallback(async () => {
    if (!userId) {
      console.log("⏱️ startTimer: no userId, skipping");
      return;
    }

    // ✅ FIX for login race: wait for hydration to complete before starting
    if (!hydrated) {
      console.log("⏱️ startTimer: waiting for hydration...");
      // Will be auto-triggered by hydration useEffect when it sees resume condition
      return;
    }

    if (isActive) return; // Already running

    // Premium = unlimited
    if (isPremium) {
      setIsActive(true);
      setRemainingSeconds(Infinity);
      setTimeExpired(false);
      return;
    }

    const today = getToday();
    const session = await getOrCreateSession();
    if (!session) return;

    // Trust already-hydrated state (which took MAX of local + remote)
    const currentRemaining = remainingSecondsRef.current;
    const currentUsed = totalSecondsUsedRef.current;

    timerDateRef.current = today;
    fractionalAccum.current = 0;
    setIsActive(true);
    setTimeExpired(currentRemaining <= 0);

    // Welcome message only on fresh start (not resume)
    if (currentUsed === 0 && onPanMessageRef.current) {
      onPanMessageRef.current({
        type: "welcome",
        message: getSmartMessage({ type: "welcome", isPremium: false }),
      });
    }
  }, [userId, isPremium, isActive, hydrated, getOrCreateSession]);

  // ── 6. Stop timer ───────────────────────────────────────
  const stopTimer = useCallback(async () => {
    setIsActive(false);
    lastTickTs.current = null;
    fractionalAccum.current = 0;

    if (tickRaf.current) {
      cancelAnimationFrame(tickRaf.current);
      tickRaf.current = null;
    }

    await syncToSupabase();
  }, [syncToSupabase]);

  // ── 7. Wall-clock tick (writes localStorage every second) ──
  useEffect(() => {
    if (!isActive || isPremium) {
      lastTickTs.current = null;
      return;
    }

    function tick(now) {
      if (lastTickTs.current === null) {
        lastTickTs.current = now;
        tickRaf.current = requestAnimationFrame(tick);
        return;
      }

      const deltaMs = now - lastTickTs.current;
      lastTickTs.current = now;

      // Guard against huge jumps (backgrounded tab)
      const clampedDelta = Math.min(deltaMs, 2000);
      fractionalAccum.current += clampedDelta / 1000;

      if (fractionalAccum.current >= 1) {
        const wholeSeconds = Math.floor(fractionalAccum.current);
        fractionalAccum.current -= wholeSeconds;

        setRemainingSeconds((prev) => {
          const next = Math.max(0, prev - wholeSeconds);

          // Warnings
          if (next <= 120 && next > 60 && !warningsSent.current.twoMinutes) {
            warningsSent.current.twoMinutes = true;
            onTimeWarningRef.current?.(120);
            onPanMessageRef.current?.({
              type: "warning",
              message: getSmartMessage({ type: "warning", secondsRemaining: 120 }),
            });
          }
          if (next <= 60 && next > 30 && !warningsSent.current.oneMinute) {
            warningsSent.current.oneMinute = true;
            onTimeWarningRef.current?.(60);
            onPanMessageRef.current?.({
              type: "warning",
              message: getSmartMessage({ type: "warning", secondsRemaining: 60 }),
            });
          }
          if (next <= 30 && next > 0 && !warningsSent.current.thirtySeconds) {
            warningsSent.current.thirtySeconds = true;
            onTimeWarningRef.current?.(30);
            onPanMessageRef.current?.({
              type: "warning",
              message: getSmartMessage({ type: "warning", secondsRemaining: 30 }),
            });
          }

          if (next === 0 && !warningsSent.current.expired) {
            warningsSent.current.expired = true;
            setTimeExpired(true);
            onTimeExpiredRef.current?.();
            onPanMessageRef.current?.({
              type: "timeExpired",
              message: "Your free time is up! 🎵 But you can finish this song and save your progress for today.",
            });
          }

          return next;
        });

        setTotalSecondsUsed((prev) => {
          const next = prev + wholeSeconds;
          // ✅ REFRESH-PROOF: write to localStorage every tick
          if (userIdRef.current) {
            writeLocalCache(userIdRef.current, getToday(), next);
          }
          return next;
        });
      }

      tickRaf.current = requestAnimationFrame(tick);
    }

    tickRaf.current = requestAnimationFrame(tick);

    return () => {
      if (tickRaf.current) {
        cancelAnimationFrame(tickRaf.current);
        tickRaf.current = null;
      }
      lastTickTs.current = null;
    };
  }, [isActive, isPremium]);

  // ── 8. Auto-sync to Supabase every 10 s ─────────────────
  useEffect(() => {
    if (!isActive || isPremium || !userId) return;

    syncIntervalRef.current = setInterval(() => {
      syncToSupabase();
    }, SYNC_INTERVAL_MS);

    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
        syncIntervalRef.current = null;
      }
    };
  }, [isActive, isPremium, userId, syncToSupabase]);

  // ── 9. Flush on page hide / unload ──────────────────────
  useEffect(() => {
    if (isPremium || !userId) return;

    const flush = () => {
      // localStorage is always current (written every tick).
      // Try to flush to Supabase too — best effort.
      if (isActiveRef.current) {
        syncToSupabase();
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") flush();
    };

    window.addEventListener("beforeunload", flush);
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.removeEventListener("beforeunload", flush);
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [isPremium, userId, syncToSupabase]);

  // ── 10. Midnight rollover → reset for new day ──────────
  useEffect(() => {
    if (isPremium) return;

    const id = setInterval(() => {
      const today = getToday();
      if (timerDateRef.current && timerDateRef.current !== today) {
        console.log("🕛 Midnight rollover — resetting timer for new day");
        timerDateRef.current = today;
        sessionDbId.current = null;
        warningsSent.current = {
          twoMinutes: false,
          oneMinute: false,
          thirtySeconds: false,
          expired: false,
        };
        setRemainingSeconds(DAILY_LIMIT_SECONDS);
        setTotalSecondsUsed(0);
        setTimeExpired(false);
        fractionalAccum.current = 0;
        if (userIdRef.current) {
          cleanupOldLocalKeys(userIdRef.current);
          getOrCreateSession();
        }
      }
    }, 30_000);

    return () => clearInterval(id);
  }, [isPremium, getOrCreateSession]);

  // ── 11. Cleanup on unmount ──────────────────────────────
  useEffect(() => {
    return () => {
      if (tickRaf.current) cancelAnimationFrame(tickRaf.current);
      if (syncIntervalRef.current) clearInterval(syncIntervalRef.current);
    };
  }, []);

  // ── Callback registration ───────────────────────────────
  const setCallbacks = useCallback(({ onTimeWarning, onTimeExpired: onExp, onPanMessage }) => {
    if (onTimeWarning !== undefined) onTimeWarningRef.current = onTimeWarning;
    if (onExp !== undefined) onTimeExpiredRef.current = onExp;
    if (onPanMessage !== undefined) onPanMessageRef.current = onPanMessage;
  }, []);

  // ── Context value ───────────────────────────────────────
  const value = {
    // State
    isActive,
    remainingSeconds,
    totalSecondsUsed,
    isPremium,
    premiumLoaded,
    hydrated,
    timeExpired,
    hasTimeLeft: remainingSeconds > 0,
    lastSyncTime,

    // Controls
    startTimer,
    stopTimer,
    setCallbacks,
    setTimeExpired,
  };

  return (
    <PracticeTimerContext.Provider value={value}>
      {children}
    </PracticeTimerContext.Provider>
  );
}

export default PracticeTimerProvider;